/** @jsx jsx */
/** @jsxFrag */
import type { FC } from 'react';
import React, { forwardRef, Children, cloneElement, useCallback } from 'react';
import { styled, cssMap, jsx } from '@compiled/react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl-next';
import type { MultiValueGenericProps } from 'react-select';

import type {
	OptionType,
	StylesConfig,
	ValueContainerProps,
	InputProps,
	ControlProps,
	SelectProps,
	OptionProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { components, mergeStyles } from '@atlaskit/select';
const { ValueContainer, Input, Control, MultiValueContainer, MultiValueLabel, Option } = components;
import CreatableSelect from '@atlaskit/select/CreatableSelect';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import EditorAddIcon from '@atlaskit/icon/core/add';

import { LoadableLazy } from '@confluence/loadable';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { fg } from '@confluence/feature-gating';
import { ADVANCED_SEARCH } from '@confluence/named-routes/entry-points/routes.ts';

import { getPrivacySafePrefix } from './useLabelsSelectProps.tsx';
import { LabelsSelectPlaceholder } from './ViewPageLabelsLoader.tsx';
import { VIEW_PAGE_LABELS_METRIC } from './perf.config.ts';

const LabelsQueryErrorDisplayLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LabelsSelectErrors" */ './LabelsSelectErrors.tsx'))
			.LabelsQueryErrorDisplay,
});
const LabelsMutationErrorDisplayLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LabelsSelectErrors" */ './LabelsSelectErrors.tsx'))
			.LabelsMutationErrorDisplay,
});
const LabelsValidationErrorLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LabelsSelectErrors" */ './LabelsSelectErrors.tsx'))
			.LabelsValidationError,
});

const i18n = defineMessages({
	inputPlaceholder: {
		id: 'labels.labels-select.input-placeholder',
		defaultMessage: 'Search or create a label',
		description:
			'Placeholder for html input element that allows users to search or create a new label in the labels selector.',
	},
	createNewLabel: {
		id: 'labels.labels-select.create-new-label',
		defaultMessage: 'Create new label',
		description:
			'Text that appears in labels select before a label. Clicking on the item creates a new label for the page.',
	},
	addLabelPlaceholder: {
		id: 'labels.view-page-labels.placeholder',
		defaultMessage: 'Add label',
		description:
			'Placeholder text that appears in the labels text on view page. Upon clicking near this text in the labels select field, the labels selector will open.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelSelectOption = styled.span({
	display: 'inline-block',
	font: token('font.body'),
	padding: `0 ${token('space.050')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	backgroundColor: token('color.background.inverse.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.labels__option--is-focused &': {
		backgroundColor: token('color.background.inverse.subtle.pressed'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateLabelContainer = styled.div({
	display: 'flex',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateLabelText = styled.div({
	flexShrink: 0,
});

export interface CustomOptionType extends OptionType {
	name: string;
	prefix: string;
	strategy: string;
	subStrategy?: string[];
}

const multiValueStyles = cssMap({
	root: {
		'&:focus-within': {
			boxShadow: `0 0 0 2px ${token('color.border.focused')}`,
			zIndex: 1,
			backgroundColor: token('color.background.neutral.pressed'),
		},
		margin: token('space.050'),
		backgroundColor: token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover a, &:focus-within a': {
			textDecoration: 'none',
		},
		pointerEvents: 'auto',
		overflow: 'hidden',
		flexDirection: 'row-reverse',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.labels__multi-value__label': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover, &:hover span': {
				backgroundColor: token('color.background.neutral.pressed'),
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.labels__multi-value__remove': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				backgroundColor: token('color.background.accent.red.subtler'),
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'a > .labels__multi-value__label': {
			color: token('color.text'),
		},
	},
});

const inputStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		input: {
			fontFamily: 'inherit',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'&::placeholder': {
				color: token('color.text.subtle'),
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			width: 'auto !important', // needed after upgrading react-select to a version that no longer uses react-input-autosize
		},
	},
});

const defaultStyles: StylesConfig<CustomOptionType, true> = {
	valueContainer: (provided) => ({
		...provided,
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		display: 'flex',
	}),
	group: (provided) => ({
		...provided,
		paddingTop: '0',
	}),
	groupHeading: (provided) => ({
		...provided,
		font: token('font.heading.xxsmall'),
		marginBottom: token('space.100'),
	}),
	multiValueLabel: (provided) => ({
		...provided,
		font: token('font.body'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.025'),
		maxWidth: '400px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	}),
	multiValue: (provided) => ({
		...provided,
		margin: token('space.050'),
		backgroundColor: token('color.background.neutral'),
		'&:focus-within': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		'&:hover a, &:focus-within a': {
			textDecoration: 'none',
		},
		pointerEvents: 'auto',
		overflow: 'hidden',
		flexDirection: 'row-reverse',
		'.labels__multi-value__label': {
			'&:hover, &:hover span': {
				backgroundColor: token('color.background.neutral.pressed'),
			},
		},
		'.labels__multi-value__remove': {
			'&:hover': {
				backgroundColor: token('color.background.accent.red.subtler'),
			},
		},
		'a > .labels__multi-value__label': {
			color: token('color.text'),
		},
	}),
	option: (provided) => ({
		...provided,
		width: 'max-content',
		minWidth: '100%',
	}),
	input: (provided) => ({
		...provided,
		input: {
			fontFamily: 'inherit',
			'::placeholder': {
				color: token('color.text.subtle'),
			},
			width: 'auto !important', // needed after upgrading react-select to a version that no longer uses react-input-autosize
		},
	}),
};

const getDisabledStyles = (isDisabled): StylesConfig<CustomOptionType, true> => {
	return isDisabled
		? {
				control: (provided) => ({
					...provided,
					'&:hover, &:focus, &:active, &:focus-within': {
						borderColor: token('color.border.inverse'),
					},
					'&:focus-within, :hover': {
						borderColor: token('color.border.inverse'),
					},
					cursor: 'default !important',
					borderWidth: '0px',
				}),
				multiValueLabel: (provided) => ({
					...provided,
					paddingRight: token('space.050'),
				}),
			}
		: {};
};

export const CustomControl: FC<ControlProps<CustomOptionType, true>> = ({ children, ...props }) => {
	const { isFocused } = props;
	return (
		<Control {...props}>
			{Children.map(children, (child: any) => {
				if (child?.type === CustomValueContainer) {
					return cloneElement(child, { isFocused });
				}
				return child;
			})}
		</Control>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomPlaceholder = styled.div({
	color: token('color.text.subtle'),
});

const AddLabelPlaceholder: FC = () => {
	return (
		<Flex alignItems="center">
			<EditorAddIcon label="" color="currentColor" spacing="spacious" />
			<FormattedMessage {...i18n.addLabelPlaceholder} />
		</Flex>
	);
};

type CustomValueContainerProps = ValueContainerProps<CustomOptionType, true> & {
	isFocused?: boolean;
};
const CustomValueContainer: FC<CustomValueContainerProps> = ({ children, isFocused, ...props }) => {
	return (
		<ValueContainer {...props}>
			{Children.map(children, (child: any) => {
				if (child?.type === CustomInput) {
					return (
						<>
							{!isFocused && (
								<CustomPlaceholder>
									<AddLabelPlaceholder />
								</CustomPlaceholder>
							)}
							{cloneElement(child, { isFocused })}
						</>
					);
				}
				return child;
			})}
		</ValueContainer>
	);
};

const CustomInput: FC<InputProps<CustomOptionType, true>> = ({
	// @ts-expect-error isFocused is not provided by react-select
	isFocused,
	...props
}) => {
	const intl = useIntl();
	return (
		// @ts-ignore Type '{ xcss: CompiledStyles<RemapMedia<{ input: { fontFamily: string; '::placeholder': { color: "var(--ds-text-subtle)"; }; width: string; }; maxWidth: "var(--ds-space-0)"; }>>; ... 311 more ...; setValue: (newValue: MultiValue<...>, action: SetValueAction, option?: CustomOptionType | undefined) => void; }' is not assignable to type '{ css?: CssFunction<void> | CssFunction<void>[]; }'.
		<Input
			{...props}
			// @ts-ignore Type '{ placeholder: string | undefined; xcss: CompiledStyles<RemapMedia<{ input: { fontFamily: string; '::placeholder': { color: "var(--ds-text-subtle)"; }; width: string; }; maxWidth: "var(--ds-space-0)"; }>>; ... 310 more ...; setValue: (newValue: MultiValue<...>, action: SetValueAction, option?: CustomOptionType | und...' is not assignable to type '{ css?: CssFunction<void> | CssFunction<void>[]; }'.
			xcss={inputStyles.root}
			// @ts-ignore these props are passed to an html input element which accepts placeholder
			placeholder={isFocused ? intl.formatMessage(i18n.inputPlaceholder) : undefined}
		/>
	);
};

// @atlaskit/select does not reexport the types we need: https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/components/MultiValue.tsx#L92
const CustomMultiValueContainer: any = ({ children, ...props }) => {
	return (
		// @ts-ignore
		<MultiValueContainer {...props} xcss={multiValueStyles.root}>
			{Children.toArray(children).reverse()}
		</MultiValueContainer>
	);
};

type CustomMultiValueLabelProps = MultiValueGenericProps<CustomOptionType> & {
	spaceKey: string | undefined;
	source: string;
};
const CustomMultiValueLabel: FC<CustomMultiValueLabelProps> = ({ spaceKey, source, ...props }) => {
	let labelsHref;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'label',
				source,
				attributes: {
					privacySafePrefix: getPrivacySafePrefix(props.data.prefix),
				},
			},
		}).fire();
	}, [source, props.data.prefix, createAnalyticsEvent]);

	try {
		if (fg('confluence_frontend_labels_in_footer')) {
			labelsHref = ADVANCED_SEARCH.toUrl(
				{},
				{
					query: {
						product: 'confluence',
						labels: props.data.value,
					},
				},
			);
		} else {
			labelsHref = `/wiki/label/${spaceKey}/${props.data.value}`;
		}
	} catch (e) {
		// just in case, fallback to this so that we don't blow up the labels section
		labelsHref = '#';
	}

	return (
		// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions
		<div onMouseDown={(e) => e.stopPropagation()}>
			<Tooltip content={String(props.data.value).length >= 50 ? props.data.value : ''}>
				{(tooltipProps) => (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={labelsHref} {...tooltipProps} onClick={handleClick}>
						{/* @ts-ignore react-select types */}
						<MultiValueLabel {...props} />
					</a>
				)}
			</Tooltip>
		</div>
	);
};

const CustomOption: FC<OptionProps<CustomOptionType, true>> = ({ children, ...props }) => {
	const {
		data: { __isNew__ },
		value,
	} = props;
	return (
		<Option {...props}>
			{__isNew__ ? (
				<CreateLabelContainer>
					<CreateLabelText>
						<FormattedMessage {...i18n.createNewLabel} />
					</CreateLabelText>
					<LabelSelectOption>{value}</LabelSelectOption>
				</CreateLabelContainer>
			) : (
				<LabelSelectOption>{value}</LabelSelectOption>
			)}
		</Option>
	);
};

type LabelsSelectComponentProps = SelectProps<CustomOptionType, true> & {
	source: string;
	spaceKey: string | undefined;
	experienceName: string;
	onUpdateSuccess?: () => void;
	isDisabled?: boolean;
	labelsQueryLoading?: boolean;
	labelsQueryError?: any;
	labelsMutationError?: any;
	validationError?: any;
	fetchRecommendedLabels?: any;
	isInDialog?: boolean;
	styles?: any;
	customProps?: any;
	controlledProps?: any;
	components: any;
	getNoOptionsMessage?: any;
};

export const LabelsSelectComponent: FC<LabelsSelectComponentProps> = forwardRef(
	(
		{
			isDisabled,
			labelsQueryLoading,
			labelsQueryError,
			labelsMutationError,
			validationError,
			fetchRecommendedLabels,
			experienceName,
			isInDialog,
			spaceKey,
			source,
			styles,
			customProps,
			controlledProps,
			components,
			getNoOptionsMessage,
		},
		ref,
	) => {
		const intl = useIntl();
		if (labelsQueryError) {
			return (
				<LabelsQueryErrorDisplayLoader experienceName={experienceName} error={labelsQueryError} />
			);
		}

		return (
			<>
				{isInDialog || !labelsQueryLoading ? (
					// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
					<div onMouseEnter={fetchRecommendedLabels}>
						<CreatableSelect<CustomOptionType, true>
							// still allow focus on the input when user has no permission to edit labels, unless there are none
							isDisabled={
								isDisabled &&
								Array.isArray(controlledProps?.value) &&
								controlledProps?.value?.length === 0
							}
							components={{
								DropdownIndicator: null,
								Placeholder: () => null,
								Control: CustomControl,
								...(!isDisabled && {
									ValueContainer: CustomValueContainer,
								}),
								Input: CustomInput,
								MultiValueContainer: CustomMultiValueContainer,
								MultiValueLabel: (props) => (
									//@ts-ignore react-select types
									<CustomMultiValueLabel spaceKey={spaceKey} source={source} {...props} />
								),
								...(isDisabled && {
									MultiValueRemove: () => null,
								}),
								Option: CustomOption,
								...components,
							}}
							ref={ref}
							noOptionsMessage={({ inputValue }) => getNoOptionsMessage(inputValue)}
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
							styles={mergeStyles(
								defaultStyles,

								mergeStyles(getDisabledStyles(Boolean(isDisabled)), styles || {}),
							)}
							isClearable={false}
							openMenuOnClick
							aria-label={intl.formatMessage(i18n.addLabelPlaceholder)}
							isSearchable={!isDisabled}
							closeMenuOnSelect={false}
							defaultOptions
							isMulti
							classNamePrefix="labels"
							placeholder={<AddLabelPlaceholder />}
							validationState={validationError ? 'error' : 'default'}
							{...controlledProps}
							{...customProps}
						/>
						{labelsMutationError && (
							<LabelsMutationErrorDisplayLoader error={labelsMutationError} />
						)}
						{validationError && (
							<LabelsValidationErrorLoader
								error={validationError}
								input={controlledProps.inputValue}
							/>
						)}
						{!labelsQueryLoading && <ExperienceSuccess name={experienceName} />}
						{source == 'viewPage' && <PageSegmentLoadEnd metric={VIEW_PAGE_LABELS_METRIC} />}
					</div>
				) : (
					<LabelsSelectPlaceholder />
				)}
			</>
		);
	},
) as FC<LabelsSelectComponentProps>;
